import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { RootState, AppDispatch } from "../../../store";
import { fetchProjects } from "../../../store/slices/projectsSlice";
import ProjectSectionTitle from "./ProjectSectionTitle";
import AdvancedGallery from "./GalleryWithPagination";
import MobileGallery from "./MobileGallery";
import Loader from "../../../components/base-components/Loader";
import ErrorMessage from "../../../components/base-components/Error";
import NoData from "../../../components/base-components/NoData";

const sectionVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0 },
};

interface Props {
  projectsDescription: string;
}

const Projects: React.FC<Props> = ({ projectsDescription }: Props) => {
  const dispatch = useDispatch<AppDispatch>();
  const { projects, status, error } = useSelector(
    (state: RootState) => state.projects
  );

  const baseUrl =
    process.env.REACT_APP_STRAPI_IMAGES_URL?.replace(/\/$/, "") || "";

  const { ref, inView } = useInView({ threshold: 0.1 });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    if (status === "idle") {
      dispatch(fetchProjects());
    }

    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [dispatch, status]);

  if (status === "loading") {
    return <Loader />;
  }

  if (status === "failed") {
    return <ErrorMessage message={error} />;
  }

  if (!projects || projects.length === 0) {
    return <NoData message="No projects found" />;
  }

  const galleryItems = projects.map((project) => ({
    id: project.id,
    slug: project.attributes.slug,
    title: project.attributes.title,
    properties: project.attributes.properties,
    description:
      project.attributes.shortDescription || "Description not available.",
    imageUrl: project?.attributes?.titleImage?.data?.attributes?.formats?.medium
      ?.url
      ? `${baseUrl}${project?.attributes?.titleImage?.data?.attributes?.formats?.medium?.url}`
      : `${baseUrl}${project?.attributes?.titleImage?.data?.attributes?.url}`,
  }));

  return (
    <motion.section
      ref={ref}
      className="bg-gray-100"
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={sectionVariants}
      transition={{ duration: 0.8, ease: "easeOut" }}
    >
      <div className="global-container py-[6.85rem] h-full flex flex-col">
        <motion.div
          variants={sectionVariants}
          transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
        >
          <ProjectSectionTitle
            title="Our"
            title2="Developments"
            subtitle={projectsDescription}
          />
        </motion.div>

        <motion.div
          className="flex-1"
          variants={sectionVariants}
          transition={{ duration: 1, ease: "easeOut", delay: 0.4 }}
        >
          {isMobile ? (
            <MobileGallery items={galleryItems} />
          ) : (
            <AdvancedGallery items={galleryItems} itemsPerPage={4} />
          )}
        </motion.div>
      </div>
    </motion.section>
  );
};

export default Projects;
